import { BUCKET_THRESHOLDS, type WhereField, type WhereOption } from '@parcel/filters';

export interface TierShortcut {
  label: string;
  range: [number, number];
}

/** The score's tier names as ranges: top tier 75 to 100, then 50 to 74, 25 to 49, 0 to 24. */
export function tierShortcuts(
  buckets: ReadonlyArray<{ key: string; label: string }>,
): TierShortcut[] {
  const lows = [...BUCKET_THRESHOLDS, 0];
  const highs = [100, ...BUCKET_THRESHOLDS.map((t) => t - 1)];
  return buckets.slice(0, 4).map((bucket, i) => ({
    label: bucket.label,
    range: [lows[i] ?? 0, highs[i] ?? 0],
  }));
}

/** The tier a stored range is, by name; null for a custom range. */
export function tierForRange(tiers: readonly TierShortcut[], value: unknown): TierShortcut | null {
  if (!Array.isArray(value) || value.length !== 2) return null;
  return tiers.find((t) => t.range[0] === value[0] && t.range[1] === value[1]) ?? null;
}

/** The tiers as value options for the score field: value "75-100", the range as the description. */
export function tierOptions(buckets: ReadonlyArray<{ key: string; label: string }>) {
  return tierShortcuts(buckets).map(({ label, range }) => ({
    value: `${range[0]}-${range[1]}`,
    label,
    description: `${range[0]} to ${range[1]}`,
  }));
}

/** Tier names belong to the table's own score; a linked record's score (a firm's ICP) shows plain ranges. */
export function fieldTiers(
  field: Pick<WhereField, 'key'>,
  options: { tiers?: readonly WhereOption[] },
): readonly WhereOption[] {
  return field.key === 'score' ? (options.tiers ?? []) : [];
}
